import React, { useState, useEffect, useContext, createContext } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ShoppingBag, Plus, Minus, Trash2, X, MapPin, ChevronDown, Loader2, Check, ArrowLeft, Home } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cart Context
const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('uf_cart');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('uf_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, variant = null, quantity = 1) => {
    const itemKey = variant ? `${product.id}-${variant.id}` : product.id;
    
    setCart(prev => {
      const existing = prev.find(item => item.key === itemKey);
      if (existing) {
        return prev.map(item =>
          item.key === itemKey
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, {
        key: itemKey,
        product_id: product.id,
        product_name: product.name,
        sku: variant?.sku || product.sku,
        price: (product.price + (variant?.price_adjustment || 0)) * 1.2,
        image: product.images?.[0] || variant?.images?.[0] || '',
        color: variant?.color,
        size: variant?.size,
        variant_id: variant?.id,
        quantity
      }];
    });
  };

  const updateQuantity = (key, quantity) => {
    if (quantity <= 0) {
      removeFromCart(key);
    } else {
      setCart(prev => prev.map(item =>
        item.key === key ? { ...item, quantity } : item
      ));
    }
  };

  const removeFromCart = (key) => {
    setCart(prev => prev.filter(item => item.key !== key));
  };

  const clearCart = () => setCart([]);

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, updateQuantity, removeFromCart, clearCart, cartTotal, cartCount }}>
      {children}
    </CartContext.Provider>
  );
};

// Product Card Component
const ProductCard = ({ product, onSelect }) => {
  const { addToCart } = useCart();
  const hasVariants = product.variants?.length > 0 || product.available_colors?.length > 0 || product.available_sizes?.length > 0;

  const handleQuickAdd = (e) => {
    e.stopPropagation();
    if (!hasVariants) {
      addToCart(product);
    } else {
      onSelect(product);
    }
  };

  return (
    <div 
      className="bg-uf-surface rounded-xl border border-uf-border overflow-hidden hover:border-uf-red/50 transition-all cursor-pointer group"
      onClick={() => onSelect(product)}
      data-testid={`product-card-${product.sku}`}
    >
      <div className="aspect-square bg-gray-800 relative overflow-hidden">
        {product.images?.[0] ? (
          <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-uf-text-muted">
            <ShoppingBag className="w-12 h-12" />
          </div>
        )}
        {product.stock_quantity !== null && product.stock_quantity <= 5 && product.stock_quantity > 0 && (
          <span className="absolute top-2 right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded">Low Stock</span>
        )}
        {product.stock_quantity === 0 && (
          <span className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded">Out of Stock</span>
        )}
      </div>
      <div className="p-4">
        <p className="text-xs text-uf-text-muted mb-1">{product.category}</p>
        <h3 className="text-uf-text font-semibold mb-2 line-clamp-2">{product.name}</h3>
        <div className="flex items-center justify-between">
          <span className="text-uf-red font-bold text-lg">£{(product.price * 1.2).toFixed(2)}</span>
          <button
            onClick={handleQuickAdd}
            disabled={product.stock_quantity === 0}
            className="bg-uf-red text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-uf-red-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid={`add-to-cart-${product.sku}`}
          >
            {hasVariants ? 'Select' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Product Detail Modal
const ProductModal = ({ product, onClose }) => {
  const { addToCart } = useCart();
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);

  const colors = product.available_colors || [...new Set(product.variants?.map(v => v.color).filter(Boolean))] || [];
  const sizes = product.available_sizes || [...new Set(product.variants?.map(v => v.size).filter(Boolean))] || [];

  const getSelectedVariant = () => {
    if (!product.variants?.length) return null;
    return product.variants.find(v =>
      (!colors.length || v.color === selectedColor) &&
      (!sizes.length || v.size === selectedSize)
    );
  };

  const variant = getSelectedVariant();
  const finalPrice = product.price + (variant?.price_adjustment || 0);

  const canAdd = (!colors.length || selectedColor) && (!sizes.length || selectedSize);

  const handleAdd = () => {
    addToCart(product, variant, quantity);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-uf-surface rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="product-modal">
        <div className="relative">
          <button onClick={onClose} className="absolute top-4 right-4 text-uf-text-muted hover:text-white z-10 bg-black/50 rounded-full p-2">
            <X className="w-5 h-5" />
          </button>
          <div className="aspect-video bg-gray-800">
            {product.images?.[0] ? (
              <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-uf-text-muted">
                <ShoppingBag className="w-16 h-16" />
              </div>
            )}
          </div>
        </div>
        
        <div className="p-6">
          <p className="text-sm text-uf-text-muted mb-1">{product.category} • {product.sku}</p>
          <h2 className="text-2xl font-bold text-uf-text mb-2">{product.name}</h2>
          <p className="text-uf-text-muted mb-4">{product.description}</p>
          
          {colors.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-uf-text mb-2">Color</p>
              <div className="flex flex-wrap gap-2">
                {colors.map(color => (
                  <button
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    className={`px-4 py-2 rounded-lg border text-sm transition-all ${
                      selectedColor === color
                        ? 'border-uf-red bg-uf-red/20 text-uf-red'
                        : 'border-uf-border text-uf-text hover:border-uf-red/50'
                    }`}
                    data-testid={`color-${color}`}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {sizes.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-uf-text mb-2">Size</p>
              <div className="flex flex-wrap gap-2">
                {sizes.map(size => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`w-12 h-12 rounded-lg border text-sm font-medium transition-all ${
                      selectedSize === size
                        ? 'border-uf-red bg-uf-red/20 text-uf-red'
                        : 'border-uf-border text-uf-text hover:border-uf-red/50'
                    }`}
                    data-testid={`size-${size}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-uf-border">
            <div className="flex items-center gap-4">
              <div className="flex items-center border border-uf-border rounded-lg">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-2 text-uf-text-muted hover:text-white">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-12 text-center text-uf-text font-medium">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="p-2 text-uf-text-muted hover:text-white">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <span className="text-2xl font-bold text-uf-red">£{(finalPrice * 1.2 * quantity).toFixed(2)}</span>
            </div>
            <button
              onClick={handleAdd}
              disabled={!canAdd}
              className="bg-uf-red text-white px-6 py-3 rounded-lg font-semibold hover:bg-uf-red-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="add-to-cart-modal"
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cart Sidebar
const CartSidebar = ({ isOpen, onClose, onCheckout }) => {
  const { cart, updateQuantity, removeFromCart, cartTotal, clearCart } = useCart();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-uf-surface shadow-2xl flex flex-col" data-testid="cart-sidebar">
        <div className="p-4 border-b border-uf-border flex items-center justify-between">
          <h2 className="text-xl font-bold text-uf-text">Your Cart</h2>
          <button onClick={onClose} className="text-uf-text-muted hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {cart.length === 0 ? (
            <div className="text-center py-12 text-uf-text-muted">
              <ShoppingBag className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Your cart is empty</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map(item => (
                <div key={item.key} className="flex gap-4 bg-uf-bg rounded-lg p-3" data-testid={`cart-item-${item.key}`}>
                  <div className="w-20 h-20 bg-gray-800 rounded-lg overflow-hidden flex-shrink-0">
                    {item.image ? (
                      <img src={item.image} alt={item.product_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-uf-text-muted">
                        <ShoppingBag className="w-6 h-6" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-uf-text font-medium text-sm truncate">{item.product_name}</h4>
                    {(item.color || item.size) && (
                      <p className="text-xs text-uf-text-muted">{[item.color, item.size].filter(Boolean).join(' / ')}</p>
                    )}
                    <p className="text-uf-red font-semibold mt-1">£{item.price.toFixed(2)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex items-center border border-uf-border rounded">
                        <button onClick={() => updateQuantity(item.key, item.quantity - 1)} className="p-1 text-uf-text-muted hover:text-white">
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-6 text-center text-sm text-uf-text">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.key, item.quantity + 1)} className="p-1 text-uf-text-muted hover:text-white">
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <button onClick={() => removeFromCart(item.key)} className="text-red-500 hover:text-red-400 p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {cart.length > 0 && (
          <div className="p-4 border-t border-uf-border">
            <div className="flex justify-between mb-4">
              <span className="text-uf-text-muted">Subtotal</span>
              <span className="text-uf-text font-bold">£{cartTotal.toFixed(2)}</span>
            </div>
            <button
              onClick={onCheckout}
              className="w-full bg-uf-red text-white py-3 rounded-lg font-semibold hover:bg-uf-red-hover transition-colors"
              data-testid="checkout-btn"
            >
              Checkout
            </button>
            <button onClick={clearCart} className="w-full text-uf-text-muted text-sm mt-2 hover:text-white">
              Clear Cart
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Checkout Page Component
export const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cart, cartTotal, clearCart } = useCart();
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [fulfillmentType, setFulfillmentType] = useState('click_and_collect');
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(null);
  const [memberInfo, setMemberInfo] = useState(null);
  
  // Guest checkout fields
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');

  // Delivery fields
  const [deliveryAddress, setDeliveryAddress] = useState({ line1: '', line2: '', city: '', postcode: '' });

  useEffect(() => {
    fetch(`${API_URL}/api/sites`)
      .then(res => res.json())
      .then(data => { setSites(data.items || data || []); })
      .catch(err => console.error('Failed to load sites:', err));
    
    const token = localStorage.getItem('member_token') || localStorage.getItem('uf_token');
    if (token) {
      fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(res => res.json()).then(data => {
        if (data.user) setMemberInfo(data.user);
        else if (data.first_name) setMemberInfo(data);
      }).catch(() => {});
    }
  }, []);

  // Check for returning from Stripe with success
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const orderId = searchParams.get('order_id');
    if (sessionId && orderId) {
      verifyPayment(sessionId, orderId);
    }
  }, [searchParams]);

  const verifyPayment = async (sessionId, orderId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/orders/verify-payment/${sessionId}`);
      const data = await res.json();
      if (data.payment_status === 'paid') {
        clearCart();
        setOrderComplete(data.order || { order_number: orderId, fulfillment_type: fulfillmentType });
      }
    } catch (err) {
      console.error('Payment verification error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (fulfillmentType === 'click_and_collect' && !selectedSite) {
      alert('Please select a pickup location');
      return;
    }
    if (fulfillmentType === 'delivery' && (!deliveryAddress.line1 || !deliveryAddress.city || !deliveryAddress.postcode)) {
      alert('Please enter a complete delivery address');
      return;
    }
    if (!memberInfo && (!guestName || !guestEmail)) {
      alert('Please enter your name and email');
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        member_id: memberInfo?.id || null,
        guest_name: memberInfo ? null : guestName,
        guest_email: memberInfo ? null : guestEmail,
        guest_phone: memberInfo ? null : guestPhone,
        items: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          sku: item.sku,
          color: item.color,
          size: item.size,
          variant_id: item.variant_id
        })),
        fulfillment_type: fulfillmentType,
        pickup_site_id: fulfillmentType === 'click_and_collect' ? selectedSite : null,
        delivery_address: fulfillmentType === 'delivery' ? deliveryAddress : null,
        origin_url: window.location.origin,
        delivery_cost: fulfillmentType === 'delivery' ? 4.99 : 0,
      };

      const token = localStorage.getItem('member_token') || localStorage.getItem('uf_token');
      const res = await fetch(`${API_URL}/api/orders/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(orderData)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to place order');
      }

      const order = await res.json();
      
      // If Stripe checkout URL returned, redirect to Stripe
      if (order.checkout_url) {
        window.location.href = order.checkout_url;
        return;
      }
      
      // If no Stripe, show order confirmation directly
      setOrderComplete(order);
      clearCart();
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Show loading when verifying payment from Stripe redirect
  const isVerifying = searchParams.get('session_id');

  if (isVerifying && !orderComplete) {
    return (
      <div className="min-h-screen bg-uf-bg flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-uf-red mx-auto mb-4" />
          <p className="text-uf-text">Verifying your payment...</p>
        </div>
      </div>
    );
  }
  
  if (cart.length === 0 && !orderComplete) {
    return (
      <div className="min-h-screen bg-uf-bg py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-uf-text-muted" />
          <h1 className="text-2xl font-bold text-uf-text mb-4">Your cart is empty</h1>
          <button onClick={() => navigate('/shop')} className="text-uf-red hover:underline" data-testid="continue-shopping-empty">
            Continue Shopping
          </button>
          <div className="mt-4">
            <Link to="/" className="text-uf-text-muted hover:text-uf-text text-sm inline-flex items-center gap-1">
              <Home className="w-4 h-4" /> Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (orderComplete) {
    return (
      <div className="min-h-screen bg-uf-bg py-12 px-4">
        <div className="max-w-2xl mx-auto text-center" data-testid="order-confirmation">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-uf-text mb-2">Order Placed!</h1>
          <p className="text-uf-text-muted mb-6">Order #{orderComplete.order_number}</p>
          
          <div className="bg-uf-surface rounded-xl border border-uf-border p-6 text-left mb-6">
            <h3 className="font-semibold text-uf-text mb-4 flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-uf-red" />
              {orderComplete.fulfillment_type === 'delivery' ? 'Delivery' : 'Click & Collect'}
            </h3>
            {orderComplete.fulfillment_type === 'delivery' ? (
              <>
                <p className="text-uf-text">Delivering to your address</p>
                <p className="text-uf-text-muted text-sm mt-2">We'll send tracking details once dispatched.</p>
              </>
            ) : (
              <>
                <p className="text-uf-text">{orderComplete.pickup_site_name}</p>
                <p className="text-uf-text-muted text-sm mt-2">We'll notify you when your order is ready for pickup.</p>
              </>
            )}
          </div>
          
          <div className="flex gap-4 justify-center">
            <button onClick={() => navigate('/shop')} className="bg-uf-red text-white px-6 py-3 rounded-lg font-semibold hover:bg-uf-red-hover">
              Continue Shopping
            </button>
            {memberInfo && (
              <button onClick={() => navigate('/hub')} className="border border-uf-border text-uf-text px-6 py-3 rounded-lg font-semibold hover:border-uf-red">
                View Orders
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-uf-bg py-8 px-4" data-testid="checkout-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => navigate('/shop')} className="p-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text-muted hover:text-uf-text" data-testid="checkout-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-bold text-uf-text">Checkout</h1>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {/* Order Details */}
          <div className="md:col-span-2 space-y-6">
            {/* Contact Info */}
            {!memberInfo && (
              <div className="bg-uf-surface rounded-xl border border-uf-border p-6">
                <h2 className="text-lg font-semibold text-uf-text mb-4">Contact Information</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-uf-text-muted mb-1">Name *</label>
                    <input
                      type="text"
                      value={guestName}
                      onChange={e => setGuestName(e.target.value)}
                      className="w-full bg-gray-100 border border-gray-300 rounded-lg px-4 py-2 text-black placeholder-gray-400"
                      data-testid="guest-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-uf-text-muted mb-1">Email *</label>
                    <input
                      type="email"
                      value={guestEmail}
                      onChange={e => setGuestEmail(e.target.value)}
                      className="w-full bg-gray-100 border border-gray-300 rounded-lg px-4 py-2 text-black placeholder-gray-400"
                      data-testid="guest-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-uf-text-muted mb-1">Phone</label>
                    <input
                      type="tel"
                      value={guestPhone}
                      onChange={e => setGuestPhone(e.target.value)}
                      className="w-full bg-gray-100 border border-gray-300 rounded-lg px-4 py-2 text-black placeholder-gray-400"
                      data-testid="guest-phone"
                    />
                  </div>
                </div>
              </div>
            )}
            
            {memberInfo && (
              <div className="bg-uf-surface rounded-xl border border-uf-border p-6">
                <h2 className="text-lg font-semibold text-uf-text mb-2">Contact Information</h2>
                <p className="text-uf-text">{memberInfo.first_name} {memberInfo.last_name}</p>
                <p className="text-uf-text-muted">{memberInfo.email}</p>
              </div>
            )}
            
            {/* Fulfilment Type */}
            <div className="bg-uf-surface rounded-xl border border-uf-border p-6">
              <h2 className="text-lg font-semibold text-uf-text mb-4">Fulfilment Method</h2>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <button
                  type="button"
                  onClick={() => setFulfillmentType('click_and_collect')}
                  data-testid="fulfillment-collect"
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all text-sm ${
                    fulfillmentType === 'click_and_collect'
                      ? 'border-uf-red bg-uf-red/10 text-uf-text'
                      : 'border-uf-border text-uf-text-muted hover:border-uf-text-muted'
                  }`}
                >
                  <MapPin className="w-5 h-5" />
                  Click & Collect
                  <span className="text-xs opacity-60">Free</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFulfillmentType('delivery')}
                  data-testid="fulfillment-delivery"
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all text-sm ${
                    fulfillmentType === 'delivery'
                      ? 'border-uf-red bg-uf-red/10 text-uf-text'
                      : 'border-uf-border text-uf-text-muted hover:border-uf-text-muted'
                  }`}
                >
                  <ShoppingBag className="w-5 h-5" />
                  Delivery
                  <span className="text-xs opacity-60">+£4.99</span>
                </button>
              </div>

              {fulfillmentType === 'click_and_collect' && (
                <div>
                  <label className="block text-sm text-uf-text-muted mb-2">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Select Pickup Location
                  </label>
                  <div>
                    <select
                      value={selectedSite}
                      onChange={e => setSelectedSite(e.target.value)}
                      className="w-full bg-uf-surface border border-uf-border rounded-lg px-4 py-3 text-uf-text"
                      data-testid="pickup-location"
                    >
                      <option value="">Choose your gym...</option>
                      {sites.map(site => (
                        <option key={site.id} value={site.id}>{site.name} - {site.city}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {fulfillmentType === 'delivery' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-uf-text-muted mb-1">Address Line 1 *</label>
                    <input
                      type="text"
                      value={deliveryAddress.line1}
                      onChange={e => setDeliveryAddress({ ...deliveryAddress, line1: e.target.value })}
                      placeholder="123 High Street"
                      className="w-full bg-uf-bg border border-uf-border rounded-lg px-4 py-2 text-uf-text"
                      data-testid="delivery-line1"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-uf-text-muted mb-1">Address Line 2</label>
                    <input
                      type="text"
                      value={deliveryAddress.line2}
                      onChange={e => setDeliveryAddress({ ...deliveryAddress, line2: e.target.value })}
                      placeholder="Flat 4"
                      className="w-full bg-uf-bg border border-uf-border rounded-lg px-4 py-2 text-uf-text"
                      data-testid="delivery-line2"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-uf-text-muted mb-1">City *</label>
                      <input
                        type="text"
                        value={deliveryAddress.city}
                        onChange={e => setDeliveryAddress({ ...deliveryAddress, city: e.target.value })}
                        placeholder="Leeds"
                        className="w-full bg-uf-bg border border-uf-border rounded-lg px-4 py-2 text-uf-text"
                        data-testid="delivery-city"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-uf-text-muted mb-1">Postcode *</label>
                      <input
                        type="text"
                        value={deliveryAddress.postcode}
                        onChange={e => setDeliveryAddress({ ...deliveryAddress, postcode: e.target.value })}
                        placeholder="LS1 2AB"
                        className="w-full bg-uf-bg border border-uf-border rounded-lg px-4 py-2 text-uf-text"
                        data-testid="delivery-postcode"
                        required
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Payment Info */}
            <div className="bg-uf-surface rounded-xl border border-uf-border p-6">
              <h2 className="text-lg font-semibold text-uf-text mb-4">Payment</h2>
              <p className="text-uf-text-muted text-sm">
                You will be redirected to a secure Stripe checkout page to complete your payment after clicking "Pay Now".
              </p>
            </div>
          </div>
          
          {/* Order Summary */}
          <div>
            <div className="bg-uf-surface rounded-xl border border-uf-border p-6 sticky top-4">
              <h2 className="text-lg font-semibold text-uf-text mb-4">Order Summary</h2>
              <div className="space-y-3 mb-4">
                {cart.map(item => (
                  <div key={item.key} className="flex justify-between text-sm">
                    <div className="flex-1">
                      <p className="text-uf-text">{item.product_name}</p>
                      {(item.color || item.size) && (
                        <p className="text-uf-text-muted text-xs">{[item.color, item.size].filter(Boolean).join(' / ')}</p>
                      )}
                      <p className="text-uf-text-muted text-xs">Qty: {item.quantity}</p>
                    </div>
                    <span className="text-uf-text">£{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-uf-border pt-4">
                <div className="flex justify-between mb-2">
                  <span className="text-uf-text-muted">Subtotal (ex. VAT)</span>
                  <span className="text-uf-text">£{(cartTotal / 1.2).toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-uf-text-muted">VAT (20%)</span>
                  <span className="text-uf-text">£{(cartTotal - cartTotal / 1.2).toFixed(2)}</span>
                </div>
                {fulfillmentType === 'delivery' && (
                  <div className="flex justify-between mb-2">
                    <span className="text-uf-text-muted">Delivery</span>
                    <span className="text-uf-text">£4.99</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold mt-4 pt-4 border-t border-uf-border">
                  <span className="text-uf-text">Total (inc. VAT)</span>
                  <span className="text-uf-red">£{(cartTotal + (fulfillmentType === 'delivery' ? 4.99 : 0)).toFixed(2)}</span>
                </div>
              </div>
              <button
                onClick={handlePlaceOrder}
                disabled={loading || (fulfillmentType === 'click_and_collect' && !selectedSite) || (fulfillmentType === 'delivery' && (!deliveryAddress.line1 || !deliveryAddress.city || !deliveryAddress.postcode))}
                className="w-full bg-uf-red text-white py-3 rounded-lg font-semibold hover:bg-uf-red-hover disabled:opacity-50 disabled:cursor-not-allowed mt-6 flex items-center justify-center"
                data-testid="place-order-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Pay Now'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Shop Page
const ShopPage = () => {
  const navigate = useNavigate();
  const { cartCount } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);

  const categories = ['all', 'clothing', 'supplements', 'accessories', 'equipment'];

  useEffect(() => {
    fetch(`${API_URL}/api/products`)
      .then(res => res.json())
      .then(data => {
        setProducts(data.items || data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const filtered = filter === 'all' 
    ? products 
    : products.filter(p => p.category?.toLowerCase() === filter);

  return (
    <div className="min-h-screen bg-uf-bg" data-testid="shop-page">
      {/* Header */}
      <div className="bg-uf-surface border-b border-uf-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-uf-text-muted hover:text-uf-text transition-colors" data-testid="shop-home-link">
              <Home className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-bold text-uf-text">UltraFlex Shop</h1>
          </div>
          <button
            onClick={() => setCartOpen(true)}
            className="relative bg-uf-red text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-uf-red-hover transition-colors"
            data-testid="open-cart-btn"
          >
            <ShoppingBag className="w-5 h-5" />
            <span>Cart</span>
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-white text-uf-red w-6 h-6 rounded-full text-sm font-bold flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Category Filters */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filter === cat
                  ? 'bg-uf-red text-white'
                  : 'bg-uf-surface border border-uf-border text-uf-text hover:border-uf-red'
              }`}
              data-testid={`filter-${cat}`}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 text-uf-red animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-uf-text-muted">
            <p>No products found in this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {filtered.map(product => (
              <ProductCard key={product.id} product={product} onSelect={setSelectedProduct} />
            ))}
          </div>
        )}
      </div>

      {/* Product Modal */}
      {selectedProduct && (
        <ProductModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
      )}

      {/* Cart Sidebar */}
      <CartSidebar 
        isOpen={cartOpen} 
        onClose={() => setCartOpen(false)} 
        onCheckout={() => { setCartOpen(false); navigate('/checkout'); }}
      />
    </div>
  );
};

export default ShopPage;
